import { pushTo } from '@app/core/routing';
import { RibbonToastSeverity, showRibbonToast } from '@mi-front/ui';
import { ReactNode, useCallback } from 'react';
import { Id, toast, ToastContent, ToastContentProps, ToastOptions } from 'react-toastify';

/** Options for the built-in link shown below the toast; useToast handles closing the toast on click. */
export interface ToastLinkOptions {
  /** Link label (e.g. "Ir al fragmento"). */
  text: string;
  /** Route or URL for the link. */
  to: string;
}

/** Toast options with optional link (text + url, toast closes on click) or custom action. */
export interface ToastOptionsWithAction<TData = unknown> extends ToastOptions<TData> {
  /**
   * Built-in link below the toast. Pass text and URL; useToast renders the link and closes the toast on click.
   */
  link?: ToastLinkOptions;
  /**
   * Custom React node or (onClose) => ReactNode for advanced cases. Ignored when `link` is set.
   */
  action?: ReactNode | ((onClose: () => void) => ReactNode);
}

interface ShowPersistentToastOptions {
  /** Render function that receives onClose; call it to dismiss the toast (e.g. from a button). */
  render: (onClose: () => void) => ReactNode;
  /** Optional toast id to update an existing toast instead of creating a new one */
  toastId?: string | number;
}

/** Opens an in-app path via the router, or an absolute http(s) URL in a new tab. */
function openToastLink(to: string): void {
  if (to.startsWith('http://') || to.startsWith('https://')) {
    window.open(to, '_blank', 'noopener,noreferrer');
    return;
  }
  if (to.startsWith('/') && !to.startsWith('//')) {
    pushTo(to);
    return;
  }
  window.location.assign(to);
}

/** True when `openToastLink` would open a new browser tab. */
function toastLinkOpensInNewTab(to: string): boolean {
  return to.startsWith('http://') || to.startsWith('https://');
}

function resolveRichContent<TData>(
  content: ToastContent<TData>,
  closeToast: () => void,
): ReactNode {
  if (typeof content === 'function') {
    return (content as (p: ToastContentProps<TData>) => ReactNode)({
      closeToast,
      data: undefined as TData,
      toastProps: {} as ToastContentProps<TData>['toastProps'],
    } as ToastContentProps<TData>);
  }
  return content as ReactNode;
}

/**
 * App toast API backed by the shared ribbon toast chrome (same look as Knock notifications).
 */
export function useToast() {
  const showPersistentToast = useCallback(({ render, toastId }: ShowPersistentToastOptions) => {
    return showRibbonToast({
      severity: 'info',
      autoClose: false,
      toastId,
      children: (onClose) => render(onClose),
    });
  }, []);

  const showTypedToast = useCallback(
    <TData = unknown,>(
      severity: RibbonToastSeverity,
      content: ToastContent<TData>,
      options?: ToastOptionsWithAction<TData>,
    ): Id => {
      const { link, action, autoClose, toastId } = options ?? {};
      const resolvedAutoClose =
        autoClose === false ? false : typeof autoClose === 'number' ? autoClose : undefined;

      const isPlainText = typeof content === 'string' || typeof content === 'number';

      // Plain message + optional built-in link (most call sites).
      if (isPlainText && action == null) {
        return showRibbonToast({
          severity,
          title: String(content),
          actionLabel: link?.text,
          onAction: link ? () => openToastLink(link.to) : undefined,
          opensInNewTab: link ? toastLinkOpensInNewTab(link.to) : false,
          autoClose: resolvedAutoClose,
          toastId,
        });
      }

      // Rich body and/or custom action node inside the Paper.
      return showRibbonToast({
        severity,
        title: isPlainText ? String(content) : undefined,
        actionLabel: link?.text,
        onAction: link ? () => openToastLink(link.to) : undefined,
        opensInNewTab: link ? toastLinkOpensInNewTab(link.to) : false,
        autoClose: resolvedAutoClose,
        toastId,
        children: (closeToast) => {
          const body = isPlainText ? null : resolveRichContent(content, closeToast);
          const actionNode =
            link == null && action != null
              ? typeof action === 'function'
                ? action(closeToast)
                : action
              : null;
          if (body == null && actionNode == null) {
            return null;
          }
          return (
            <>
              {body}
              {actionNode}
            </>
          );
        },
      });
    },
    [],
  );

  const notifyError = useCallback(
    <TData = unknown,>(content: ToastContent<TData>, options?: ToastOptionsWithAction<TData>) =>
      showTypedToast('error', content, options),
    [showTypedToast],
  );

  const notifySuccess = useCallback(
    <TData = unknown,>(content: ToastContent<TData>, options?: ToastOptionsWithAction<TData>) =>
      showTypedToast('success', content, options),
    [showTypedToast],
  );

  const notifyInfo = useCallback(
    <TData = unknown,>(content: ToastContent<TData>, options?: ToastOptionsWithAction<TData>) =>
      showTypedToast('info', content, options),
    [showTypedToast],
  );

  const notifyWarn = useCallback(
    <TData = unknown,>(content: ToastContent<TData>, options?: ToastOptionsWithAction<TData>) =>
      showTypedToast('warning', content, options),
    [showTypedToast],
  );

  return {
    showPersistentToast,
    notifyError,
    notifySuccess,
    notifyInfo,
    notifyWarn,
    dismiss: toast.dismiss,
  };
}
